/**
 * Tool state machine.
 *
 * Every tool is a stateful object that owns its own in-progress work
 * ("the current stroke", "the current marquee", etc.) and the canvas hands
 * it pointer events. The canvas' render loop asks the active tool to draw
 * its in-progress preview on top of the committed scene.
 *
 * Tool lifecycle per pointer action:
 *
 *     pointer down    → tool.onPointerDown(evt, store)
 *     pointer move    → tool.onPointerMove(evt, store)
 *     pointer up      → tool.onPointerUp(evt, store)
 *     every frame     → tool.drawPreview?(ctx, store)
 *     tool changed    → tool.cancel?() on the outgoing tool
 *
 * Events come pre-converted to *map space* — tools never see screen
 * coordinates. The canvas does the viewport math once, then hands the
 * tool a clean `{x, y, pressure, button}` structure.
 */

import type { SceneElement } from "../../lib/scene";
import type { SceneStore } from "../store/sceneStore";

export interface ToolPointerEvent {
  /** Map-space x. */
  x: number;
  /** Map-space y. */
  y: number;
  /** 0..1, 0.5 when no real pressure is reported. */
  pressure: number;
  /** PointerEvent.button (0 left, 1 middle, 2 right). */
  button: number;
  /** Shift held at event time. Used for constrain-to-axis etc. */
  shift: boolean;
  /** PointerEvent.pointerId for capture tracking. */
  pointerId: number;
}

export interface Tool {
  readonly kind: string;
  onPointerDown(evt: ToolPointerEvent, store: SceneStore): void;
  onPointerMove(evt: ToolPointerEvent, store: SceneStore): void;
  onPointerUp(evt: ToolPointerEvent, store: SceneStore): void;
  /** Optional preview layer — drawn on the live canvas every frame. */
  drawPreview?(ctx: CanvasRenderingContext2D, store: SceneStore): void;
  /** Called when the tool is swapped out or the user hits Escape. */
  cancel?(): void;
  /** True while the tool is in the middle of an interaction. */
  isActive(): boolean;
}

// ---------------------------------------------------------------------------
// Helpers shared by the creation tools
// ---------------------------------------------------------------------------

/** Build axis-aligned bbox from two corner points. */
export function bboxFromCorners(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { x: number; y: number; w: number; h: number } {
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1),
    w: Math.abs(x1 - x0),
    h: Math.abs(y1 - y0),
  };
}

/** Snap (dx, dy) to the nearest axis if shift is held. Used by line/arrow/shape. */
export function maybeSnapAxis(
  dx: number,
  dy: number,
  shift: boolean,
): { dx: number; dy: number } {
  if (!shift) return { dx, dy };
  return Math.abs(dx) >= Math.abs(dy) ? { dx, dy: 0 } : { dx: 0, dy };
}

/** Map an element (just for local use) to a flat points array. */
export function linePoints(x0: number, y0: number, x1: number, y1: number): number[] {
  // Stored relative to (x, y), which will be the bbox min.
  const minX = Math.min(x0, x1);
  const minY = Math.min(y0, y1);
  return [x0 - minX, y0 - minY, x1 - minX, y1 - minY];
}

export function bboxFromLine(x0: number, y0: number, x1: number, y1: number): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1),
    w: Math.abs(x1 - x0),
    h: Math.abs(y1 - y0),
  };
}

/** Compute a bbox tight around a flat point list. */
export function bboxFromPoints(points: number[]): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  if (points.length < 2) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = points[0],
    minY = points[1],
    maxX = points[0],
    maxY = points[1];
  for (let i = 2; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Hit-test: is (px, py) inside element bbox (with tolerance)? */
export function hitTestBBox(
  el: SceneElement,
  px: number,
  py: number,
  tol = 4,
): boolean {
  return (
    px >= el.x - tol &&
    px <= el.x + el.width + tol &&
    py >= el.y - tol &&
    py <= el.y + el.height + tol
  );
}
